import React from 'react';
import { COLORS, Panel, StatTile, Table, Banner } from '../ui';
import { GroupedBarChart } from '../charts';

/**
 * Screen 3 -- the monthly review, read on the fifth business day.
 *
 * M1 is shown per contractor rather than blended, because the contract
 * thresholds are per contractor (GVR 85%, PSA 90%) and a combined number lets a
 * failing one hide behind a passing one.
 */

const pctText = v => v == null ? '—' : `${Math.round(v * 100)}%`;
const daysText = v => v == null ? '—' : `${v.toFixed(1)}d`;

/** Contract thresholds that trigger a formal notice. */
const THRESHOLDS = [
    { match: /gilbarco|veeder/i, name: 'GVR', threshold: 0.85, ref: 'GVR Sch 1 cl 4.3' },
    { match: /\bPSA\b/i, name: 'PSA', threshold: 0.90, ref: 'PSA Sch 1 KPI reporting' },
];

const thresholdFor = contractor => THRESHOLDS.find(t => t.match.test(contractor || ''));

function ContractorTile({ row }) {
    const t = thresholdFor(row.contractor);
    const breachesThreshold = t && row.withinSlaPct != null && row.withinSlaPct <= t.threshold;
    return (
        <div style={{ position: 'relative' }}>
            <StatTile
                label={t ? t.name : (row.contractor || '').slice(0, 18)}
                value={pctText(row.withinSlaPct)}
                sub={`${row.completed} completed${t ? ` · threshold ${pctText(t.threshold)}` : ''}`}
                tone={breachesThreshold ? 'bad' : row.withinSlaPct != null ? 'good' : undefined}
            />
            {breachesThreshold && (
                <span style={{
                    position: 'absolute', top: 8, right: 8, background: COLORS.critical, color: '#fff',
                    fontSize: 9.5, fontWeight: 700, padding: '2px 6px', borderRadius: 3, letterSpacing: 0.4,
                }}>NOTICE</span>
            )}
        </div>
    );
}

export default function MonthlyScreen({ data, onOpenList }) {
    const { months, contractors, weekly, m2 } = data;
    const current = months[months.length - 1] || {};
    const previous = months[months.length - 2] || {};

    // M1 for the current month, limited to the two contract contractors plus
    // whoever else did the most work.
    const m1Rows = (current.m1 || []).slice(0, 4);

    const stageSeries = months.map(m => ({
        label: m.month,
        dwell: m.stageDays?.dwell == null ? 0 : +m.stageDays.dwell.toFixed(1),
        rectification: m.stageDays?.rectification == null ? 0 : +m.stageDays.rectification.toFixed(1),
    }));

    const weeklySeries = weekly.map(w => ({ label: w.week, opened: w.opened, closed: w.closed }));

    const contractorColumns = [
        { key: 'contractor', label: 'Contractor', render: r => (r.contractor || '').slice(0, 34) },
        { key: 'stream', label: 'Stream' },
        { key: 'completed', label: 'Completed', align: 'right' },
        {
            key: 'withinTargetPct', label: '% within target', align: 'right',
            render: r => {
                const t = thresholdFor(r.contractor);
                const bad = t && r.withinTargetPct != null && r.withinTargetPct <= t.threshold;
                return <span style={{ fontWeight: 600, color: bad ? COLORS.critical : COLORS.ink }}>
                    {pctText(r.withinTargetPct)}
                </span>;
            },
        },
        {
            key: 'medianRectificationDays', label: 'Median rectification', align: 'right',
            render: r => daysText(r.medianRectificationDays),
        },
        { key: 'followupPct', label: 'Follow-up %', align: 'right', render: r => pctText(r.followupPct) },
    ];

    const monthColumns = [
        { key: 'month', label: 'Month' },
        { key: 'm3', label: 'M3 % non-pump ≤14d', align: 'right', render: r => pctText(r.m3?.within14Pct) },
        { key: 'm3n', label: 'Closed', align: 'right', render: r => r.m3?.closed ?? 0 },
        { key: 'm4', label: 'M4 follow-up rate', align: 'right', render: r => pctText(r.m4?.followupPct) },
        { key: 'm4n', label: 'Opened', align: 'right', render: r => r.m4?.opened ?? 0 },
        { key: 'dwell', label: 'Dwell', align: 'right', render: r => daysText(r.stageDays?.dwell) },
        { key: 'approval', label: 'Approval', align: 'right', render: r => daysText(r.stageDays?.approval) },
        { key: 'rect', label: 'Rectification', align: 'right', render: r => daysText(r.stageDays?.rectification) },
        { key: 'closeout', label: 'Close-out', align: 'right', render: r => daysText(r.stageDays?.closeout) },
    ];

    return (
        <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                {m1Rows.map(row => <ContractorTile key={row.contractor} row={row} />)}
                <StatTile
                    label="M3 non-pump ≤ 14 days"
                    value={pctText(current.m3?.within14Pct)}
                    sub={`${current.m3?.closed ?? 0} closed · last month ${pctText(previous.m3?.within14Pct)}`}
                    tone={current.m3?.within14Pct != null && current.m3.within14Pct < 0.9 ? 'bad' : 'good'}
                />
                <StatTile
                    label="M4 follow-up rate"
                    value={pctText(current.m4?.followupPct)}
                    sub={`${current.m4?.opened ?? 0} opened · last month ${pctText(previous.m4?.followupPct)}`}
                    tone={current.m4?.followupPct != null && current.m4.followupPct > 0.1 ? 'bad' : 'good'}
                />
            </div>

            {m2 && !m2.available && (
                <Banner tone="info">
                    <strong>M2 First Time Fix is not shown.</strong> {m2.reason}
                </Banner>
            )}

            <Panel
                title="Month by month"
                subtitle="M3, M4 and the median business days spent in each of the four stages"
            >
                <Table columns={monthColumns} rows={[...months].reverse()} rowKey={r => r.month} maxHeight={420} />
                <div style={{ fontSize: 11.5, color: COLORS.muted, marginTop: 8 }}>
                    When M3 misses, the stage columns say which clock slipped.
                </div>
            </Panel>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))', gap: 16 }}>
                <Panel title="Stage time by month" subtitle="Median business days: dwell vs rectification, non-pump">
                    <GroupedBarChart
                        data={stageSeries}
                        seriesA={{ key: 'dwell', label: 'Dwell', color: COLORS.series[0] }}
                        seriesB={{ key: 'rectification', label: 'Rectification', color: COLORS.series[1] }}
                        maxLabel={80}
                        format={v => `${v}d`}
                    />
                </Panel>

                <Panel title="Opened vs closed per week" subtitle="With the running backlog">
                    <GroupedBarChart
                        data={weeklySeries}
                        seriesA={{ key: 'opened', label: 'Opened', color: COLORS.series[0] }}
                        seriesB={{ key: 'closed', label: 'Closed', color: COLORS.series[2] }}
                        maxLabel={90}
                    />
                    {weekly.length > 0 && (
                        <div style={{ fontSize: 12, color: COLORS.ink2, marginTop: 10 }}>
                            Backlog moved from {weekly[0].backlog} to {weekly[weekly.length - 1].backlog} across the period.
                        </div>
                    )}
                </Panel>
            </div>

            <Panel
                title="Contractor performance"
                subtitle="Work completed or closed in the selected period"
                right={<button
                    onClick={() => onOpenList('closed', 'Closed in this period')}
                    style={{
                        background: 'none', border: `1px solid ${COLORS.link}`, color: COLORS.link,
                        padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                    }}>See the work orders</button>}
            >
                <Table columns={contractorColumns} rows={contractors} rowKey={r => r.contractor}
                    maxHeight={420} empty="Nothing was completed in this period." />
            </Panel>
        </>
    );
}
